import { X } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * The filters currently narrowing a list, each removable in place.
 *
 * <p>These are the "applied" row of the toolbar (§3): once a filter has been
 * set inside the More-filters panel it is out of sight, and a list that is
 * narrowed by something the reader cannot see reads as a short book rather than
 * a filtered one. Each chip names the filter AND its value, so the reader knows
 * what is narrowing the list without opening anything, and the ✕ removes just
 * that one.</p>
 *
 * <p>Renders nothing at all with no chips — the row is absent, not empty.</p>
 */
export interface ActiveFilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

export interface ActiveFilterChipsProps {
  chips: ActiveFilterChip[];
  /** Offered when there is more than one chip; clears all of them at once. */
  onClearAll?: () => void;
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function ActiveFilterChips({
  chips,
  onClearAll,
  label = "Active filters",
  className,
  "data-testid": testId,
}: ActiveFilterChipsProps) {
  if (chips.length === 0) return null;
  return (
    <div
      className={cn("flex flex-wrap items-center gap-space-2", className)}
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      {chips.map((chip) => (
        // `py-space-1` (4px) is the nearest spacing token to the 3px the chip was drawn at.
        <span
          key={chip.key}
          className="inline-flex items-center gap-space-1 rounded-pill border border-gold bg-gold/15 py-space-1 pl-space-3 pr-space-1 text-caption font-medium text-onyx"
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Remove filter: ${chip.label}`}
            className="inline-flex h-5 w-5 items-center justify-center rounded-pill text-onyx hover:bg-onyx/10 focus-visible:outline-none focus-visible:shadow-goldRing"
          >
            <X size={12} strokeWidth={2} aria-hidden />
          </button>
        </span>
      ))}
      {onClearAll && chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-caption font-medium text-gold-shadow hover:underline focus-visible:outline-none focus-visible:shadow-goldRing"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
